import React from 'react';
import { Box, Typography, Avatar, Stack, Rating } from '@mui/material';
import { styled } from '@mui/material/styles';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

const CardContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: '32px',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderRadius: '16px',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)'
  }
}));

const QuoteIcon = styled(FormatQuoteIcon)(({ theme }) => ({
  position: 'absolute',
  top: '24px',
  right: '24px',
  fontSize: '48px',
  color: 'rgba(102, 126, 234, 0.15)',
  transform: 'rotate(180deg)'
}));

const RatingContainer = styled(Box)(({ theme }) => ({
  marginBottom: '16px',
  '& .MuiRating-icon': {
    color: '#fbbf24'
  }
}));

const TestimonialText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '15px',
  fontWeight: 400,
  lineHeight: '1.7',
  color: '#4a5568',
  marginBottom: '24px',
  flex: 1,
  fontStyle: 'italic',
  position: 'relative',
  zIndex: 1
}));

const ReviewerInfo = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '16px',
  paddingTop: '20px',
  borderTop: '1px solid rgba(0, 0, 0, 0.08)'
}));

const ReviewerAvatar = styled(Avatar)(({ theme }) => ({
  width: '56px',
  height: '56px',
  border: '3px solid #667eea',
  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
  backgroundColor: '#667eea',
  color: '#ffffff',
  fontWeight: 600,
  fontSize: '20px'
}));

const ReviewerDetails = styled(Stack)(({ theme }) => ({
  flex: 1,
  gap: '4px'
}));

const ReviewerName = styled(Typography)(({ theme }) => ({
  fontFamily: 'Montserrat, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  color: '#1a202c',
  lineHeight: '1.2'
}));

const ReviewerRole = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 400,
  color: '#718096',
  lineHeight: '1.2'
}));

const VerifiedBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 10px',
  backgroundColor: '#10b981',
  color: '#ffffff',
  borderRadius: '12px',
  fontSize: '11px',
  fontWeight: 600,
  fontFamily: 'Inter, sans-serif',
  letterSpacing: '0.5px'
}));

const TestimonialCard = ({ testimonial }) => {
  // Get first letter of name for avatar fallback
  const avatarLetter = testimonial.name?.charAt(0).toUpperCase() || '?';
  
  return (
    <CardContainer>
      <QuoteIcon />
      
      <RatingContainer>
        <Rating
          value={testimonial.rating || 5}
          readOnly
          size="small"
        />
      </RatingContainer>

      <TestimonialText>
        "{testimonial.review || testimonial.content}"
      </TestimonialText>

      <ReviewerInfo>
        <ReviewerAvatar
          src={testimonial.image}
          alt={testimonial.name}
        >
          {!testimonial.image && avatarLetter}
        </ReviewerAvatar>
        
        <ReviewerDetails>
          <ReviewerName>{testimonial.name}</ReviewerName>
          <ReviewerRole>{testimonial.role || 'Customer'}</ReviewerRole>
          {testimonial.isRealReview && (
            <VerifiedBadge>
              ✓ Verified Review
            </VerifiedBadge>
          )}
        </ReviewerDetails>
      </ReviewerInfo>
    </CardContainer>
  );
};

export default TestimonialCard;